import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    CircularProgress,
    Container,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Stack,
    Typography,
    Alert,
    Snackbar,
    FormHelperText,
    Paper
} from '@mui/material';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'utils/axios';

async function verifyResetToken(uid, token) {
    const res = await axios.get(`password/verify/${uid}/${token}/`);
    if (res.status !== 200) return { valid: false };
    return res.data;
}

async function submitNewPassword(uid, token, new_password, confirm_password) {
    const res = await axios.post(`password/reset/`, {
        uid,
        token,
        new_password,
        confirm_password
    });
    return res;
}

// ---- Password helpers ----
const hasLowercase = (s) => /[a-z]/.test(s);
const hasUppercase = (s) => /[A-Z]/.test(s);
const hasNumber = (s) => /\d/.test(s);
const hasSpecial = (s) => /[^A-Za-z0-9]/.test(s);

// Yup validation to enforce the same rules
const validationSchema = Yup.object({
    new_password: Yup.string()
        .min(9, 'Must be at least 9 characters')
        .test('lower', 'Must include at least 1 lowercase letter', (v) => !!v && hasLowercase(v))
        .test('upper', 'Must include at least 1 uppercase letter', (v) => !!v && hasUppercase(v))
        .test('number', 'Must include at least 1 number', (v) => !!v && hasNumber(v))
        .test('special', 'Must include at least 1 special character', (v) => !!v && hasSpecial(v))
        .required('New password is required'),
    confirm_password: Yup.string()
        .oneOf([Yup.ref('new_password')], 'Passwords do not match')
        .required('Please confirm your password')
});

export default function ResetPassword() {
    const { uid, token } = useParams();
    const navigate = useNavigate();

    const [checking, setChecking] = useState(true);
    const [valid, setValid] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [snack, setSnack] = useState(null);

    useEffect(() => {
        let cancelled = false;
        async function run() {
            if (!uid || !token) return; // wait for router hydration
            setChecking(true);
            try {
                const result = await verifyResetToken(uid, token);
                if (!cancelled) setValid(!!result.valid);
            } catch (e) {
                if (!cancelled) setValid(false);
            } finally {
                if (!cancelled) setChecking(false);
            }
        }
        run();
        return () => {
            cancelled = true;
        };
    }, [uid, token]);

    return (
        <>
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Paper elevation={1} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
                    <Stack spacing={2}>
                        <Typography variant="h4" fontWeight={700}>
                            Reset your password
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Choose a new password for your account.
                        </Typography>

                        {checking && (
                            <Box display="flex" alignItems="center" gap={2} mt={1}>
                                <CircularProgress size={20} />
                                <Typography variant="body2">Validating reset link…</Typography>
                            </Box>
                        )}

                        {!checking && valid === false && (
                            <Alert severity="error">
                                This reset link is invalid or has expired. Please request a new one from the{' '}
                                <strong>Forgot password</strong> page.
                            </Alert>
                        )}

                        {!checking && valid && (
                            <Formik
                                initialValues={{ new_password: '', confirm_password: '' }}
                                validationSchema={validationSchema}
                                onSubmit={async (values, { setSubmitting }) => {
                                    if (!uid || !token) return;
                                    const res = await submitNewPassword(uid, token, values.new_password, values.confirm_password);
                                    setSubmitting(false);
                                    if (res.status === 200) {
                                        setSnack({ open: true, message: 'Password updated. You can now log in.', severity: 'success' });
                                        setTimeout(() => navigate('/login'), 1200);
                                    } else {
                                        setSnack({ open: true, message: res.data.message, severity: 'error' });
                                    }
                                }}
                            >
                                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => {
                                    const pw = values.new_password || '';
                                    return (
                                        <form onSubmit={handleSubmit} noValidate>
                                            <Stack spacing={2.5}>
                                                <Box>
                                                    <InputLabel htmlFor="new-password">New password</InputLabel>
                                                    <OutlinedInput
                                                        id="new-password"
                                                        name="new_password"
                                                        type={showPassword ? 'text' : 'password'}
                                                        value={values.new_password}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        fullWidth
                                                        error={Boolean(touched.new_password && errors.new_password)}
                                                        placeholder="Enter a strong password"
                                                        endAdornment={
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => setShowPassword((s) => !s)}
                                                                    edge="end"
                                                                    aria-label="toggle password visibility"
                                                                >
                                                                    {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        }
                                                    />
                                                    {touched.new_password && errors.new_password && (
                                                        <FormHelperText error>{errors.new_password}</FormHelperText>
                                                    )}
                                                </Box>

                                                <Box>
                                                    <InputLabel htmlFor="confirm-password">Confirm password</InputLabel>
                                                    <OutlinedInput
                                                        id="confirm-password"
                                                        name="confirm_password"
                                                        type={showConfirm ? 'text' : 'password'}
                                                        value={values.confirm_password}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        fullWidth
                                                        error={Boolean(touched.confirm_password && errors.confirm_password)}
                                                        placeholder="Retype your new password"
                                                        endAdornment={
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => setShowConfirm((s) => !s)}
                                                                    edge="end"
                                                                    aria-label="toggle confirm password visibility"
                                                                >
                                                                    {showConfirm ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        }
                                                    />
                                                    {touched.confirm_password && errors.confirm_password && (
                                                        <FormHelperText error>{errors.confirm_password}</FormHelperText>
                                                    )}
                                                </Box>

                                                {/* Live password rules checklist */}
                                                <Box
                                                    component="ul"
                                                    sx={{
                                                        mt: 1.5,
                                                        pl: 3,
                                                        color: 'text.secondary',
                                                        '& li': { lineHeight: 1.7 },
                                                        '& li.checked': { color: 'success.main', fontWeight: 600 }
                                                    }}
                                                >
                                                    <li className={pw.length > 8 ? 'checked' : ''}>Must be at least 9 characters</li>
                                                    <li className={hasLowercase(pw) ? 'checked' : ''}>At least 1 lowercase letter</li>
                                                    <li className={hasUppercase(pw) ? 'checked' : ''}>At least 1 uppercase letter</li>
                                                    <li className={hasNumber(pw) ? 'checked' : ''}>At least 1 number</li>
                                                    <li className={hasSpecial(pw) ? 'checked' : ''}>At least 1 special character</li>
                                                </Box>
                                                <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
                                                    {isSubmitting ? 'Updating…' : 'Reset password'}
                                                </Button>
                                            </Stack>
                                        </form>
                                    );
                                }}
                            </Formik>
                        )}
                    </Stack>
                </Paper>

                <Snackbar
                    open={!!snack?.open}
                    autoHideDuration={4000}
                    onClose={() => setSnack(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    {snack ? (
                        <Alert onClose={() => setSnack(null)} severity={snack.severity} sx={{ width: '100%' }}>
                            {snack.message}
                        </Alert>
                    ) : null}
                </Snackbar>
            </Container>
        </>
    );
}
